<template>
  <div>
    <VTableRemote
      ref="tableRemoteRef"
      :iconHeader="hasPermission('accessGroups.add') ? 'add' : null"
      :items="displayedAccessGroup"
      isSearchable
      :sortableColumns="['name']"
      :headers="tableHeaders"
      :loading="loading"
      :title="$t('accessGroups.pageName')"
      :itemActions="itemActions"
      :pagination="pagination"
      :isInfinitScroll="true"
      hideDefaultFooter
      :isCustomIcon="true"
      :centerColumns="['action']"
      :notSortableColumns="['action']"
      @iconAction="onClickAction"
      @sendQueryParams="fetchQuery"
    >
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </VTableRemote>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import AdminAccessGroupForm from '@/components/forms/AdminAccessGroupForm.vue';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import { cloneDeep, isEmpty } from 'lodash';
import perpage from '@/constants/pagination';

export default {
    components: {
        VTableRemote,
    },
    data() {
        return {
            loading: false,
            accessGroups: [],
            pagination: {},
            queryParams: {
                perPage: perpage,
                search: '',
            },
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'userLang',
            'lang',
            'teamMainLang',
        ]),
        itemActions() {
            const actions = [];
            if (this.hasPermission('accessGroups.view')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                });
            }
            if (this.hasPermission('accessGroups.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }
            return actions;
        },
        tableHeaders() {
            return [
                'name',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: 'name',
            }));
        },
        displayedAccessGroup() {
            return this.accessGroups.map(ag => {
                let displayName = ag.name[this.lang] || ag.name[this.teamMainLang];
                if (!displayName) {
                    const availableNames = Object.values(ag.name);
                    displayName = availableNames.find(name => name);
                }

                return {
                    ...ag,
                    name: displayName,
                };
            });
        },


    },
    created() {
        this.getAccessGroups();
    },
    methods: {
        async getAccessGroups() {
            if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                this.loading = true;
                const { data } = await this.$rq.getAccessGroups(this.queryParams);
                this.accessGroups = [...this.accessGroups, ...data.accessGroups];
                this.pagination = data.pagination;
                this.loading = false;
            }
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('accessGroups.add'),
                showLangs: true,
                component: Vue.component('AdminAccessGroupForm', AdminAccessGroupForm),
                onConfirm: requestPayload => this.addRequest(requestPayload),
            });
        },
        onClickEdit(item) {
            const accessGroup = this.accessGroups.find(group => group.id === item.id);
            if (accessGroup) {
                this.$root.$emit('showModal', {
                    title: this.$t('accessGroups.update'),
                    showLangs: true,
                    component: Vue.component('AdminAccessGroupForm', AdminAccessGroupForm),
                    payload: {
                        populateWith: cloneDeep(accessGroup),
                    },
                    onConfirm: requestPayload => {
                        this.handleUpdate(requestPayload);
                    },
                });
            }
        },
        async addRequest(requestPayload) {
            const response = await this.$rq.addAccessGroup(requestPayload);
            this.refreshAccessGroups();
            this.$toasted.global.toast_success({
                message: this.$t('msg.accessGroupAdded', {
                    data: response.name,
                }),
            });
        },
        onClickAction(item, action) {
            if (item === 'add') {
                this.onClickAdd();
            } else if (action) {
                switch (action.icon) {
                    case 'delete':
                        this.handleRemove(item);
                        break;
                    case 'edit':
                        this.onClickEdit(item);
                        break;
                    default:
                }
            }
        },
        async removeRequest(accessGroup) {
            const { id } = accessGroup;
            await this.$rq.deleteAccessGroup(id);
            await this.refreshAccessGroups();
            this.$toasted.global.toast_success({
                message: this.$t('msg.accessGroupRemoved', {
                    data: accessGroup.name,
                }),
            });
        },
        handleRemove(accessGroup) {
            this.$root.$emit('showModal', {
                title: this.$t('accessGroups.remove'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: {
                    msg: this.$t('accessGroups.confirmRemove'),
                },
                onConfirm: () => this.removeRequest(accessGroup),
                isForm: false,
            });
        },
        async handleUpdate(accessGroup) {
            const { id } = accessGroup;
            const payload = {
                clients: accessGroup.clients,
                members: accessGroup.members,
                name: accessGroup.name,
                groupLeaders: accessGroup.groupLeaders,
            };
            await this.$rq.updateAccessGroup(id, payload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.accessGroupUpdated', {
                    data: accessGroup.name[this.userLang],
                }),
            });
            await this.refreshAccessGroups();
        },
        async fetchQuery(queryParams) {
            this.queryParams = { ...this.queryParams, ...queryParams, page: 1 };
            this.pagination = {};
            this.accessGroups = [];
            await this.getAccessGroups();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getAccessGroups();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
        async refreshAccessGroups() {
            this.pagination = {};
            this.accessGroups = [];
            this.queryParams = { page: 1 };
            await this.fetchQuery({});
        },
    },
};
</script>
